import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  nanoid, PayloadAction,
  ThunkDispatch,
  UnknownAction,
} from "@reduxjs/toolkit";
import { format, formatDate } from "date-fns";
import {
  createCommandAsyncThunk,
  createQueryAsyncThunk,
} from "../../app/createApiAsyncThunk";
import type { LocallyHandledErrors } from "../../app/middleware";
import { RootState } from "../../app/store";
import { isoFormat, today } from "../../common/helpers/formatDateTime";
import { EventPayload } from "../../common/types/Event";
import { Paginated } from "../../common/types/Paginated";
import { EezyCompany } from "../invoices/invoiceAPI";
import { Skill, WorkTitle } from "../orders/orderAPI";
import { PayslipsResponse } from "../salaries/payslips/payslipAPI";
import { LOCAL_STORAGE_KEY } from "../talentProfile/constants";
import { mapAssignmentSupplementPriceOverridesRows } from "../talentProfile/Employment/AssignmentAdditionalInfo/AssignmentSupplementsPriceOverrides/assignmentSupplementsPriceOverridesMapper";
import { sortPermissions } from "../talentProfile/talentSelfServiceOrderPermissions/helpers";
import {
  AssignmentSupplementPriceOverridesChangedV2Payload,
  changeAssignmentSupplementPriceOverrides,
  changeAssignmentSupplementPriceSchedule,
  ChangeAssignmentSupplementPriceScheduleCmd,
  ChangeOverrides,
  PriceOverridesChanged,
} from "../tesPricingOverride/tesPricingOverrideAPI";
import {
  fetchExpertUsers,
  selectAllExpertUsers,
} from "../user/expertUsersSlice";
import { CoreUser } from "../user/types/entities";
import { CoreUsersQueryParameters } from "../user/types/queries";
import { getCoreUsers } from "../user/userAPI";
import { fetchAttachments } from "./helpers";
import {
  addAssignmentSupplement,
  addSelfServiceOrderPermission as addSelfServiceOrderPermissionAPI,
  approveIntroducedTalentWithoutOrder,
  AssignmentById,
  AssignmentCostCentersChanged,
  AssignmentRangeUpdated,
  AssignmentInvPriceChanged,
  AssignmentOrderAttributeEvents,
  AssignmentSalaryOverrideChanged,
  AssignmentSupplementPriceOverridesOverviewQuery,
  AssignmentSupplementPriceSchedule,
  AssignmentSupplementPriceScheduleQuery,
  AssignmentVariationSelection,
  blacklistsByTalentId,
  cancelTalentIntroductionWithoutOrder,
  candidateIntroductions,
  certificateDefinitionsAutoCompleteQuery,
  CertificateDocument,
  certificateDocumentDownloadUrl,
  certificateDocumentUploadUrl,
  certificateUpload,
  ChangeAssignmentInvPrice,
  ChangeAssignmentOrderAttributes,
  ChangeAssignmentSalaryOverride,
  chgAssignmentCostCenters,
  chgAssignmentInvPrice,
  chgAssignmentOrdAttr,
  chgAssignmentSalaryOverride,
  ChgAssignmentTesSel,
  chgAssignmentTesSel,
  createExclusivityEntryCmd,
  deleteAssignmentSupplement,
  editAssignmentSupplement,
  EezyCompanyAutocompleteFilters,
  EmploymentDetailsForManualSalaryResponse,
  EmploymentsHierarchyResponse,
  endAssignment as endAssignmentAPI,
  EnhancedChangeAssignmentCostCenters,
  exclusivitiesByTalentId,
  Gender,
  getAssignmentById,
  getAssignmentSupplementPriceOverrideScheduleByCode,
  getAssignmentSupplementPriceOverridesOverview,
  getEezyCompanyAutocomplete,
  getEmploymentDetailsForManualSalary,
  getEmploymentsHierarchy,
  getManualSalariesByEmployment,
  getPayslipsOverview,
  getSalaryPaymentInfo,
  getTalentById,
  getTalentCVDownloadUrl,
  getTalentCVUploadUrl,
  getTalentJobApplications,
  getTalentLogs,
  getTalentPreviewById,
  getTalentSelfServiceOrderPermissions,
  getTalentUnionPayments,
  getWorkAgreementTemplateData,
  introduceTalentsToCustomerWithoutOrder,
  listTalents,
  LocalStorageAttachment,
  ManualSalariesResponse,
  postAddBlacklistEntry,
  postAddManualSalary,
  postAddTalentCertificate,
  postAddTalentTraining,
  postApproveTalentCertificate,
  PostAssignmentSupplement,
  postCreateTalentUnionPayment,
  postDeleteManualSalary,
  postDeleteTalentUnionPayment,
  postEditManualSalary,
  postRejectTalentCertificate,
  postRemoveBlacklistEntry,
  postRemoveTalentCertificate,
  postRemoveTalentCertificateDocument,
  postRemoveTalentTraining,
  PostSplitAssignment,
  postUpdateTalentAccount,
  postUpdateTalentAddress,
  postUpdateTalentApplicationLanguage,
  postUpdateTalentCertificate,
  postUpdateTalentCoverLetter,
  postUpdateTalentEducations,
  postUpdateTalentGdprProperties,
  postUpdateTalentLanguages,
  postUpdateTalentRecommenders,
  postRemoveTalentSsn,
  postRemoveTalentBankInfo,
  postUpdateTalentSalaryInformation,
  postUpdateTalentSkills,
  postUpdateTalentStatus,
  postUpdateTalentTaxDeductionCard,
  postUpdateTalentTaxInformation,
  postUpdateTalentTraining,
  postUpdateTalentUnionPayment,
  postUpdateTalentWorkRight,
  postUpdateTalentWorkTitles,
  removeExclusivityEntryCmd,
  removeSelfServiceOrderPermission as removeSelfServiceOrderPermissionAPI,
  removeTalentCVDocument,
  runTradeUnionsAutocompleteQuery,
  SalaryPaymentInfo,
  searchTalentsAutocomplete,
  searchTalentsFull,
  SetAssignmentEnd,
  setTalentRecruiter,
  setTalentWorkingAreaFromHome,
  splitAssignment,
  Talent,
  TalentAccount,
  TalentAddress,
  TalentAddressResponse,
  TalentBlacklist,
  TalentCertificate,
  TalentCertificateDocument,
  TalentCoverLetterPayload,
  TalentExclusivity,
  TalentFilters,
  talentHrRepresentativesAutocomplete,
  TalentJobApplicationsParams,
  TalentJobApplicationsResponse,
  TalentPreview,
  talentProfilePictureDownloadUrl,
  TalentsAutocompleteFilters,
  TalentsFullSearchFilters,
  TalentSkillsPayload,
  TalentTraining,
  TalentUnionCreatePayment,
  TalentUnionPayment,
  TalentVehicleAvailableUpdated,
  TalentWorkTitlesPayload,
  TradeUnionsAutocompleteFilters,
  trainingDefinitionsQuery,
  UpdateAssignmentRange,
  updateAssignmentRange as updateAssignmentRangeAPI,
  updateBlacklistEntryCmd,
  UpdateContinuousEmploymentRange,
  updateContinuousEmploymentRange as updateContinuousEmploymentRangeAPI,
  updateExclusivityEntryCmd,
  updateSelfServiceOrderPermission as updateSelfServiceOrderPermissionAPI,
  updateTalentEezyReview,
  updateTalentHrRepresentative,
  UpdateTalentVehicleAvailable,
  updateTalentVehicleAvailable,
  uploadTalentCV,
  WorkAgreementTemplateData,
  WorkAgreementTemplateDataParams,
} from "./talentAPI";
import {
  AddBlacklistEntry,
  AddSelfServiceOrderPermission,
  AddTalentCertificate,
  AddTalentTraining,
  ApproveIntroducedTalentWithoutOrder,
  ApproveTalentCertificate,
  CancelTalentIntroductionWithoutOrder,
  CreateExclusivityEntry,
  CreateManualSalary,
  DeleteManualSalary,
  EditManualSalary,
  IntroduceTalentsToCustomerWithoutOrder,
  RejectTalentCertificate,
  RemoveBlacklistEntry,
  RemoveExclusivityEntry,
  RemoveSelfServiceOrderPermission,
  RemoveTalentCertificate,
  RemoveTalentCertificateDocument,
  RemoveTalentTraining,
  SetTalentRecruiter,
  SetTalentWorkingAreaFromHome,
  UpdateBlacklistEntry,
  UpdateExclusivityEntry,
  UpdateSelfServiceOrderPermission,
  UpdateTalentAccount,
  UpdateTalentApplicationLanguage,
  UpdateTalentCertificate,
  UpdateTalentEducations,
  UpdateTalentEezyReview,
  UpdateTalentGdprProperties,
  UpdateTalentHrRepresentative,
  UpdateTalentLanguages,
  UpdateTalentRecommenders,
  UpdateTalentSalaryInformation,
  UpdateTalentStatus,
  UpdateTalentTaxDeductionCard,
  UpdateTalentTaxInformation,
  UpdateTalentTraining,
  UpdateTalentWorkRight,
} from "./types/commands";
import { UpdateTalentHrRepresentativeThunk } from "./types/common";
import {
  BlacklistEntryAdded,
  BlacklistEntryRemoved,
  BlacklistEntryUpdated,
  CandidateWithoutOrderApprovedByCustomer,
  CandidateWithoutOrderIntroducedToCustomer,
  CandidateWithoutOrderIntroductionCancelled,
  ExclusivityEntryAdded,
  ExclusivityEntryRemoved,
  ExclusivityEntryUpdated,
  ManualSalaryAdded,
  ManualSalaryEdited,
  SelfServiceOrderPermissionAdded,
  SelfServiceOrderPermissionUpdated,
  TalentApplicationLanguageUpdated,
  TalentBankInfoUpdated,
  TalentCertificateAdded,
  TalentCertificateApproved,
  TalentCertificateDocumentRemoved,
  TalentCertificateRejected,
  TalentCertificateRemoved,
  TalentCertificateUpdated,
  TalentEducationsUpdated,
  TalentEezyReviewUpdated,
  TalentGdprPropertiesUpdated,
  TalentGenderUpdated,
  TalentHrRepresentativeUpdated,
  TalentLanguagesUpdated,
  TalentRecommendersUpdated,
  TalentRecruiterUpdated,
  TalentSalaryInformationUpdated,
  TalentSelfServiceOrderPermissionDeleted,
  TalentSsnUpdated,
  TalentStatusUpdated,
  TalentTaxDeductionCardUpdated,
  TalentTaxInformationUpdated,
  TalentTrainingAdded,
  TalentTrainingRemoved,
  TalentTrainingUpdated,
  TalentWorkingAreaEvents,
  TalentWorkingAreaRadiusFromHomeLocationUpdated,
  TalentWorkRightUpdated,
} from "./types/events";
import {
  CandidateIntroductionsByTalentIdFilters,
  CertificateDefinition,
  CertificateDefinitionsFilters,
  CertificateDocumentDownloadUrlQuery,
  CertificateDocumentUploadUrlQuery,
  CvDocumentUploadUrlQuery,
  DocumentDownloadUrl,
  DocumentUploadUrl,
  ExclusivitiesByTalentIdFilters,
  TalentCvParams,
  TalentHrRepresentativeAutocomplete,
  TalentHrRepresentativesAutocompleteFilters,
  TalentIdQueryParameter,
  TalentIntroduction,
  TalentLogEvent,
  TalentProfilePictureDownloadUrl,
  TalentProfilePictureDownloadUrlQuery,
  TalentSelfServiceOrderPermission,
  TrainingDefinition,
  TrainingDefinitionsFilters,
} from "./types/queries";

type Entities = {
  [key: string]: any;
};

export type SliceState = {
  ids: [];
  entities: Entities;
  cursor: string[] | null;
  assignment: AssignmentById;
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
  certificateDefinitions: CertificateDefinition[];
  certificateDefinitionsStatus: "idle" | "pending" | "succeeded" | "failed";
};

const talentsAdapter = createEntityAdapter<Talent>({
  sortComparer: (a, b) =>
    a.account?.lastName.localeCompare(b.account?.lastName),
});

export const fetchTalents = createAsyncThunk<
  Talent[],
  TalentFilters,
  { state: RootState }
>("talents/fetchTalents", async (filters: TalentFilters, { requestId }) => {
  const res = await listTalents({ requestId });
  return res.data;
});

const enrichWithHrRepresentatives = async (
  res: Paginated<Talent[]>,
  getState: () => RootState,
  dispatch: ThunkDispatch<RootState, unknown, UnknownAction>,
  requestId: string,
): Promise<Paginated<Talent[]>> => {
  const state = getState();
  let expertUsers = selectAllExpertUsers(state);

  if (expertUsers.length === 0) {
    const expertUsersResponse = await dispatch(
      fetchExpertUsers({ requestId }),
    ).unwrap();
    expertUsers = expertUsersResponse.data;
  }

  const enrichedTalents: Talent[] = res.data.map((talent) => {
    const expertUser = expertUsers.find(
      (user) => user.id === talent.hrRepresentative?.id,
    );
    return {
      ...talent,
      ...(talent.hrRepresentative && {
        hrRepresentative: {
          ...talent.hrRepresentative,
          firstName: expertUser?.account.firstName,
          lastName: expertUser?.account.lastName,
        },
      }),
    };
  });

  return { ...res, data: enrichedTalents };
};

export const fetchTalentsAutocomplete = createAsyncThunk<
  Paginated<Talent[]>,
  TalentsAutocompleteFilters,
  { state: RootState }
>(
  "talents/fetchTalentsAutocomplete",
  async (filters: TalentsAutocompleteFilters, { requestId }) => {
    const res = await searchTalentsAutocomplete(filters, { requestId });
    return res.data;
  },
);

export const fetchTalentsAutocompleteWithHr = createAsyncThunk<
  Paginated<Talent[]>,
  TalentsAutocompleteFilters,
  { state: RootState }
>(
  "talents/fetchTalentsAutocompleteWithHr",
  async (
    filters: TalentsAutocompleteFilters,
    { requestId, getState, dispatch },
  ) => {
    const res = await dispatch(fetchTalentsAutocomplete(filters)).unwrap();
    return enrichWithHrRepresentatives(res, getState, dispatch, requestId);
  },
);

export const fetchTalentsPage = createAsyncThunk<
  Paginated<Talent[]>,
  TalentsFullSearchFilters,
  { state: RootState }
>(
  "talents/fetchTalentsPage",
  async (
    filters: TalentsFullSearchFilters,
    { requestId, getState, dispatch },
  ) => {
    const res = await searchTalentsFull(filters, { requestId });
    return enrichWithHrRepresentatives(res.data, getState, dispatch, requestId);
  },
);

export const fetchTalent = createAsyncThunk<
  Talent,
  TalentFilters,
  { state: RootState }
>("talents/fetchTalent", async (filters: TalentFilters, { requestId }) => {
  const res = await getTalentById(filters, { requestId });
  return res.data;
});

export const postChangeAssignmentSupplementPriceOverrides = createAsyncThunk<
  EventPayload<PriceOverridesChanged>[],
  ChangeOverrides,
  { state: RootState }
>(
  "talents/postChangeAssignmentSupplementPriceOverrides",
  async (data: ChangeOverrides, { requestId }) =>
    await changeAssignmentSupplementPriceOverrides(data, {
      requestId,
    }),
);

export const fetchAssignmentSupplementPriceOverridesOverview =
  createQueryAsyncThunk<
    ReturnType<typeof mapAssignmentSupplementPriceOverridesRows>,
    AssignmentSupplementPriceOverridesOverviewQuery
  >(
    "talents/fetchAssignmentSupplementPriceOverridesOverview",
    async (queryParams, { requestId }) => {
      const data = await getAssignmentSupplementPriceOverridesOverview(
        queryParams,
        { requestId },
      );

      return mapAssignmentSupplementPriceOverridesRows(data);
    },
  );

export const fetchAssignmentSupplementPriceScheduleByCode =
  createQueryAsyncThunk<
    AssignmentSupplementPriceSchedule | null,
    AssignmentSupplementPriceScheduleQuery
  >(
    "talents/fetchAssignmentSupplementPriceScheduleByCode",
    async (queryParams, { requestId }) =>
      getAssignmentSupplementPriceOverrideScheduleByCode(queryParams, {
        requestId,
      }),
  );

export const postChangeAssignmentSupplementPriceSchedule =
  createCommandAsyncThunk<
    ChangeAssignmentSupplementPriceScheduleCmd,
    AssignmentSupplementPriceOverridesChangedV2Payload
  >(
    "talents/postChangeAssignmentSupplementPriceSchedule",
    changeAssignmentSupplementPriceSchedule,
  );

export const fetchTalentCertificateDocuments = createAsyncThunk<
  Talent,
  TalentFilters,
  { state: RootState }
>(
  "talents/fetchTalentCertificateDocuments",
  async (filters: TalentFilters, { requestId }) => {
    const res = await getTalentById(filters, { requestId });
    return res.data;
  },
);

export const fetchTalentCv = createAsyncThunk<
  Talent,
  TalentFilters,
  { state: RootState }
>("talents/fetchTalentCv", async (filters: TalentFilters, { requestId }) => {
  const res = await getTalentById(filters, { requestId });
  return res.data;
});

export const fetchEmploymentsHierarchy = createAsyncThunk<
  EmploymentsHierarchyResponse,
  string,
  { state: RootState }
>(
  "talents/fetchEmploymentsHierarchy",
  async (talentId: string, { requestId }) => {
    const res = await getEmploymentsHierarchy(talentId, { requestId });
    return res.data;
  },
);

export const fetchPayslipsOverview = createAsyncThunk<
  PayslipsResponse,
  string,
  { state: RootState }
>("talents/fetchPayslipsOverview", async (talentId: string, { requestId }) => {
  const res = await getPayslipsOverview(talentId, { requestId });
  return res.data;
});

export const fetchTalentUnionPayments = createAsyncThunk<
  TalentUnionPayment[],
  string,
  { state: RootState }
>(
  "talents/fetchTalentUnionPayments",
  async (talentId: string, { requestId }) => {
    const res = await getTalentUnionPayments(talentId, { requestId });
    return res.data;
  },
);

export const createTalentUnionPayment = createAsyncThunk<
  TalentUnionCreatePayment[],
  TalentUnionCreatePayment,
  { state: RootState }
>(
  "talents/createTalentUnionPayment",
  async (payment: TalentUnionCreatePayment, { requestId }) => {
    const res = await postCreateTalentUnionPayment(payment, { requestId });
    return res.data;
  },
);

export const updateTalentUnionPayment = createAsyncThunk<
  TalentUnionPayment[],
  TalentUnionPayment,
  { state: RootState }
>(
  "talents/updateTalentUnionPayment",
  async (payment: TalentUnionPayment, { requestId }) => {
    const res = await postUpdateTalentUnionPayment(payment, { requestId });
    return res.data;
  },
);

export const deleteTalentUnionPayment = createAsyncThunk<
  any,
  string,
  { state: RootState }
>("talents/deleteTalentUnionPayment", async (paymentId, { requestId }) => {
  const res = await postDeleteTalentUnionPayment(paymentId, { requestId });
  return res.data;
});

export const fetchAssignmentById = createAsyncThunk<
  AssignmentById,
  string,
  { state: RootState }
>(
  "talents/fetchAssignmentById",
  async (assignmentId: string, { requestId }) => {
    const res = await getAssignmentById(assignmentId, { requestId });
    return res.data;
  },
);

export const postSplitAssignment = createAsyncThunk<
  any,
  PostSplitAssignment,
  { state: RootState }
>(
  "talents/splitAssignment",
  async (splitAssignmentData: PostSplitAssignment, { requestId }) => {
    const res = await splitAssignment(splitAssignmentData, { requestId });
    return res.data;
  },
);

export const changeAssignmentTesSel = createAsyncThunk<
  EventPayload<AssignmentVariationSelection>,
  ChgAssignmentTesSel,
  { state: RootState }
>(
  "assignment/changeAssignmentTesSel",
  async (
    payload,
    { requestId },
  ): Promise<EventPayload<AssignmentVariationSelection>> =>
    await chgAssignmentTesSel(payload, { requestId }),
);

export const postAddAssignmentSupplement = createAsyncThunk<
  any,
  PostAssignmentSupplement,
  { state: RootState }
>("talents/addAssignmentSupplement", async (supplementData, { requestId }) => {
  const response = await addAssignmentSupplement(supplementData, {
    requestId,
  });
  return response.data;
});

export const postEditAssignmentSupplement = createAsyncThunk<
  any,
  PostAssignmentSupplement,
  { state: RootState }
>("talents/editAssignmentSupplement", async (supplementData, { requestId }) => {
  const response = await editAssignmentSupplement(supplementData, {
    requestId,
  });
  return response.data;
});

export const postDeleteAssignmentSupplement = createAsyncThunk<
  any,
  PostAssignmentSupplement,
  { state: RootState }
>(
  "talents/deleteAssignmentSupplement",
  async (supplementData, { requestId }) => {
    const response = await deleteAssignmentSupplement(supplementData, {
      requestId,
    });
    return response.data;
  },
);

export const fetchSalaryPaymentInfo = createAsyncThunk<
  SalaryPaymentInfo,
  string,
  { state: RootState }
>("talents/fetchSalaryPaymentInfo", async (talentId: string, { requestId }) => {
  const res = await getSalaryPaymentInfo(talentId, { requestId });
  return res.data;
});

export const updateTalentAccountAddress = createAsyncThunk(
  "talents/updateTalentAccountAddress",
  async (
    data: { talentAddress: TalentAddress; talentId: string },
    { getState, requestId },
  ): Promise<TalentAddressResponse> => {
    const { talents } = getState() as any;

    const version = talents.entities[data.talentId].version;

    return await postUpdateTalentAddress(
      data.talentAddress,
      data.talentId,
      version,
      { requestId },
    );
  },
);

export const updateTalentAccount = createAsyncThunk<
  EventPayload<Partial<TalentAccount>>[],
  UpdateTalentAccount,
  { state: RootState }
>(
  "talents/updateTalentAccount",
  async (
    payload,
    { requestId },
  ): Promise<EventPayload<Partial<TalentAccount>>[]> => {
    return await postUpdateTalentAccount(payload, { requestId });
  },
);

export const updateTalentWorkRight = createAsyncThunk(
  "talents/updateTalentWorkRight",
  async (
    command: UpdateTalentWorkRight,
    { requestId },
  ): Promise<EventPayload<TalentWorkRightUpdated>> =>
    await postUpdateTalentWorkRight(command, { requestId }),
);

export const updateTalentCoverLetter = createAsyncThunk(
  "talents/updateTalentAccountCoverLetter",
  async (
    payload: TalentCoverLetterPayload,
    { requestId },
  ): Promise<EventPayload<TalentCoverLetterPayload>> => {
    return await postUpdateTalentCoverLetter(payload, { requestId });
  },
);

export const updateTalentWorkTitles = createAsyncThunk(
  "talents/updateTalentWorkTitles",
  async (
    data: { talentId: string; version: number; workTitles: WorkTitle[] },
    { requestId },
  ): Promise<EventPayload<TalentWorkTitlesPayload>> => {
    return await postUpdateTalentWorkTitles(data, { requestId });
  },
);

export const updateTalentSkills = createAsyncThunk(
  "talents/updateTalentSkills",
  async (
    data: { talentId: string; version: number; skills: Skill[] },
    { requestId },
  ): Promise<EventPayload<TalentSkillsPayload>> => {
    return await postUpdateTalentSkills(data, { requestId });
  },
);

export const updateTalentEducations = createAsyncThunk(
  "talents/updateTalentEducations",
  async (
    data: UpdateTalentEducations,
    { requestId },
  ): Promise<EventPayload<TalentEducationsUpdated>> => {
    return await postUpdateTalentEducations(data, { requestId });
  },
);

export const updateTalentRecommenders = createAsyncThunk(
  "talents/updateTalentRecommenders",
  async (
    data: UpdateTalentRecommenders,
    { requestId },
  ): Promise<EventPayload<TalentRecommendersUpdated>> => {
    return await postUpdateTalentRecommenders(data, { requestId });
  },
);

export const updateTalentLanguages = createAsyncThunk(
  "talents/updateTalentLanguages",
  async (
    data: UpdateTalentLanguages,
    { requestId },
  ): Promise<EventPayload<TalentLanguagesUpdated>> => {
    return await postUpdateTalentLanguages(data, { requestId });
  },
);

export const addTalentCertificate = createAsyncThunk<
  EventPayload<TalentCertificateAdded>[],
  AddTalentCertificate,
  { state: RootState }
>(
  "talents/addTalentCertificate",
  async (
    data: AddTalentCertificate,
    { requestId },
  ): Promise<EventPayload<TalentCertificateAdded>[]> => {
    return await postAddTalentCertificate(data, { requestId });
  },
);

export const getCertificateDocumentUploadUrl = createAsyncThunk<
  DocumentUploadUrl,
  CertificateDocumentUploadUrlQuery,
  { state: RootState }
>(
  "talents/certificateDocumentUploadUrl",
  async (
    queryParams: CertificateDocumentUploadUrlQuery,
    { requestId },
  ): Promise<DocumentUploadUrl> =>
    await certificateDocumentUploadUrl(queryParams, { requestId }),
);

export const getCertificateDocumentDownloadUrl = createAsyncThunk<
  DocumentDownloadUrl,
  CertificateDocumentDownloadUrlQuery,
  { state: RootState }
>(
  "talents/certificateDocumentDownloadUrl",
  async (
    queryParams: CertificateDocumentDownloadUrlQuery,
    { requestId },
  ): Promise<DocumentDownloadUrl> =>
    await certificateDocumentDownloadUrl(queryParams, { requestId }),
);

export const getTalentProfilePictureDownloadUrl = createAsyncThunk<
  TalentProfilePictureDownloadUrl,
  TalentProfilePictureDownloadUrlQuery,
  { state: RootState }
>(
  "talents/profilePictureDownloadUrl",
  async (
    queryParams: TalentProfilePictureDownloadUrlQuery,
    { requestId },
  ): Promise<TalentProfilePictureDownloadUrl> =>
    await talentProfilePictureDownloadUrl(queryParams, { requestId }),
);

export const addTalentTraining = createAsyncThunk(
  "talents/addTalentTraining",
  async (
    command: AddTalentTraining,
    { requestId },
  ): Promise<EventPayload<TalentTrainingAdded>> =>
    await postAddTalentTraining(command, { requestId }),
);

export const updateTalentTraining = createAsyncThunk(
  "talents/updateTalentTraining",
  async (
    command: UpdateTalentTraining,
    { requestId },
  ): Promise<EventPayload<TalentTrainingUpdated>> =>
    await postUpdateTalentTraining(command, { requestId }),
);

export const removeTalentTraining = createAsyncThunk(
  "talents/removeTalentTraining",
  async (
    command: RemoveTalentTraining,
    { requestId },
  ): Promise<EventPayload<TalentTrainingRemoved>> =>
    await postRemoveTalentTraining(command, { requestId }),
);

export const updateTalentApplicationLanguage = createAsyncThunk(
  "talents/updateTalentApplicationLanguage",
  async (
    data: UpdateTalentApplicationLanguage,
    { requestId },
  ): Promise<EventPayload<TalentApplicationLanguageUpdated>> => {
    return await postUpdateTalentApplicationLanguage(data, { requestId });
  },
);

export const updateTalentGdprProperties = createAsyncThunk(
  "talents/updateTalentGdprProperties",
  async (
    data: UpdateTalentGdprProperties,
    { requestId },
  ): Promise<EventPayload<TalentGdprPropertiesUpdated>> => {
    return await postUpdateTalentGdprProperties(data, { requestId });
  },
);

export const uploadCertificate = createAsyncThunk<
  any, // TODO: check
  CertificateDocument,
  { state: RootState }
>(
  "talents/certificateUpload",
  async (certificateDocument: CertificateDocument) => {
    return await certificateUpload(
      certificateDocument.url,
      certificateDocument.content,
    );
  },
);

export const updateTalentCertificate = createAsyncThunk<
  EventPayload<TalentCertificateUpdated>,
  UpdateTalentCertificate,
  { state: RootState }
>(
  "talents/updateTalentCertificate",
  async (
    body: UpdateTalentCertificate,
    { requestId },
  ): Promise<EventPayload<TalentCertificateUpdated>> => {
    return await postUpdateTalentCertificate(body, { requestId });
  },
);

export const removeTalentCertificate = createAsyncThunk<
  EventPayload<TalentCertificateRemoved>,
  RemoveTalentCertificate,
  { state: RootState }
>(
  "talents/removeTalentCertificate",
  async (
    body: RemoveTalentCertificate,
    { requestId },
  ): Promise<EventPayload<TalentCertificateRemoved>> => {
    return await postRemoveTalentCertificate(body, { requestId });
  },
);

export const removeTalentCertificateDocument = createAsyncThunk<
  EventPayload<TalentCertificateDocumentRemoved>[],
  RemoveTalentCertificateDocument,
  { state: RootState }
>(
  "talents/removeTalentCertificateDocument",
  async (
    body: RemoveTalentCertificateDocument,
    { requestId },
  ): Promise<EventPayload<TalentCertificateDocumentRemoved>[]> => {
    return await postRemoveTalentCertificateDocument(body, { requestId });
  },
);

export const approveTalentCertificate = createAsyncThunk<
  EventPayload<TalentCertificateApproved>,
  ApproveTalentCertificate,
  { state: RootState }
>(
  "talents/approveTalentCertificate",
  async (
    body: ApproveTalentCertificate,
    { requestId },
  ): Promise<EventPayload<TalentCertificateApproved>> => {
    return await postApproveTalentCertificate(body, { requestId });
  },
);

export const rejectTalentCertificate = createAsyncThunk<
  EventPayload<TalentCertificateRejected>,
  RejectTalentCertificate,
  { state: RootState }
>(
  "talents/rejectTalentCertificate",
  async (
    body: RejectTalentCertificate,
    { requestId },
  ): Promise<EventPayload<TalentCertificateRejected>> => {
    return await postRejectTalentCertificate(body, { requestId });
  },
);

export const removeTalentSsn = createCommandAsyncThunk(
  "talents/removeTalentSsn",
  postRemoveTalentSsn,
);

export const removeTalentBankInfo = createCommandAsyncThunk(
  "talents/removeTalentBankInfo",
  postRemoveTalentBankInfo,
);

export const updateTalentSalaryInformation = createAsyncThunk(
  "talents/updateTalentSalaryInformation",
  async (
    command: UpdateTalentSalaryInformation,
    { requestId },
  ): Promise<EventPayload<TalentSalaryInformationUpdated>[]> =>
    await postUpdateTalentSalaryInformation(command, {
      requestId,
    }),
);

export const updateTalentTaxInformation = createAsyncThunk(
  "talents/updateTalentTaxInformation",
  async (
    command: UpdateTalentTaxInformation,
    { requestId },
  ): Promise<EventPayload<TalentTaxInformationUpdated>> =>
    await postUpdateTalentTaxInformation(command, {
      requestId,
    }),
);

export const updateTalentTaxDeductionCard = createAsyncThunk(
  "talents/updateTalentTaxDeductionCard",
  async (
    command: UpdateTalentTaxDeductionCard,
    { requestId },
  ): Promise<EventPayload<TalentTaxDeductionCardUpdated>> =>
    await postUpdateTalentTaxDeductionCard(command, {
      requestId,
    }),
);

export const searchTalentHrRepresentatives = createAsyncThunk<
  TalentHrRepresentativeAutocomplete[],
  TalentHrRepresentativesAutocompleteFilters,
  { state: RootState }
>(
  "talents/searchTalentHrRepresentatives",
  async (filters: TalentHrRepresentativesAutocompleteFilters, { requestId }) =>
    await talentHrRepresentativesAutocomplete(filters, { requestId }),
);

export const updateTalentHrRep = createAsyncThunk<
  EventPayload<TalentHrRepresentativeUpdated>,
  UpdateTalentHrRepresentativeThunk,
  { state: RootState }
>(
  "talents/updateTalentHrRep",
  async (params: UpdateTalentHrRepresentative, { requestId }) =>
    await updateTalentHrRepresentative(params, { requestId }),
);

export const updateEezyReview = createAsyncThunk<
  EventPayload<TalentEezyReviewUpdated>,
  UpdateTalentEezyReview,
  { state: RootState }
>(
  "talents/updateEezyReview",
  async (params: UpdateTalentEezyReview, { requestId }) =>
    await updateTalentEezyReview(params, { requestId }),
);

export const fetchManualSalariesByEmployment = createAsyncThunk<
  ManualSalariesResponse,
  string,
  { state: RootState }
>("talents/fetchManualSalariesByEmployment", async (employmentId: string) => {
  const res = await getManualSalariesByEmployment(employmentId);
  return res;
});

export const fetchEmploymentDetailsForManualSalary = createAsyncThunk<
  EmploymentDetailsForManualSalaryResponse,
  string,
  { state: RootState }
>(
  "talents/fetchEmploymentDetailsForManualSalary",
  async (employmentId: string) => {
    const res = await getEmploymentDetailsForManualSalary(employmentId);
    return res;
  },
);

export const addManualSalary = createAsyncThunk(
  "talents/addManualSalary",
  async (
    command: CreateManualSalary,
    { requestId },
  ): Promise<EventPayload<ManualSalaryAdded>> =>
    await postAddManualSalary(command, {
      requestId,
    }),
);

export const editManualSalary = createAsyncThunk(
  "talents/editManualSalary",
  async (
    command: EditManualSalary,
    { requestId },
  ): Promise<EventPayload<ManualSalaryEdited>> =>
    await postEditManualSalary(command, {
      requestId,
    }),
);

export const deleteManualSalary = createAsyncThunk(
  "talents/deleteManualSalary",
  async (
    command: DeleteManualSalary,
    { requestId },
  ): Promise<EventPayload<{}>> =>
    await postDeleteManualSalary(command, {
      requestId,
    }),
);

export const fetchEezyCompaniesAutocomplete = createAsyncThunk<
  EezyCompany[],
  EezyCompanyAutocompleteFilters,
  { state: RootState }
>(
  "talents/eezyCompanyAutocomplete",
  async (filters: EezyCompanyAutocompleteFilters, { requestId }) =>
    await getEezyCompanyAutocomplete(filters, { requestId }),
);

export const fetchCertificateDefinitionsAutocomplete = createQueryAsyncThunk<
  CertificateDefinition[],
  CertificateDefinitionsFilters
>("talents/certificateDefinitions", certificateDefinitionsAutoCompleteQuery);

export const fetchTrainingDefinitions = createQueryAsyncThunk<
  TrainingDefinition[],
  TrainingDefinitionsFilters
>("talents/trainingDefinitions", trainingDefinitionsQuery);

export const doTradeUnionsAutocompleteQuery = createAsyncThunk<
  any,
  TradeUnionsAutocompleteFilters,
  { state: RootState }
>(
  "talents/tradeUnionsAutocomplete",
  async (filters: TradeUnionsAutocompleteFilters, { requestId }) =>
    await runTradeUnionsAutocompleteQuery(filters, { requestId }),
);

export const changeAssignmentOrderAttributes = createAsyncThunk<
  EventPayload<AssignmentOrderAttributeEvents>[],
  ChangeAssignmentOrderAttributes,
  { state: RootState }
>(
  "talents/changeAssignmentOrderAttributes",
  async (
    data,
    { requestId },
  ): Promise<EventPayload<AssignmentOrderAttributeEvents>[]> =>
    await chgAssignmentOrdAttr(data, { requestId }),
);

export const changeAssignmentCostCenters = createAsyncThunk<
  EventPayload<AssignmentCostCentersChanged>,
  EnhancedChangeAssignmentCostCenters,
  { state: RootState }
>(
  "talents/changeAssignmentCostCenters",
  async (
    data,
    { requestId },
  ): Promise<EventPayload<AssignmentCostCentersChanged>> => {
    const { custCostCenters, ...rest } = data;
    const formattedParams = {
      ...rest,
      custCostCenterIds: data.custCostCenters.map(({ id }) => id),
    };
    return await chgAssignmentCostCenters(formattedParams, { requestId });
  },
);

export const changeAssignmentSalaryOverride = createAsyncThunk<
  EventPayload<AssignmentSalaryOverrideChanged>,
  ChangeAssignmentSalaryOverride,
  { state: RootState }
>(
  "talents/changeAssignmentSalaryOverride",
  async (
    data,
    { requestId },
  ): Promise<EventPayload<AssignmentSalaryOverrideChanged>> => {
    return await chgAssignmentSalaryOverride(data, { requestId });
  },
);

export const changeAssignmentInvPrice = createAsyncThunk<
  EventPayload<AssignmentInvPriceChanged>,
  ChangeAssignmentInvPrice,
  { state: RootState }
>(
  "talents/changeAssignmentInvPrice",
  async (
    data,
    { requestId },
  ): Promise<EventPayload<AssignmentInvPriceChanged>> => {
    return await chgAssignmentInvPrice(data, { requestId });
  },
);

export const endAssignment = createAsyncThunk<
  EventPayload<AssignmentRangeUpdated>,
  SetAssignmentEnd,
  { state: RootState }
>(
  "talents/endAssignment",
  async (data, { requestId }): Promise<EventPayload<AssignmentRangeUpdated>> =>
    await endAssignmentAPI(data, { requestId }),
);

export const updateAssignmentRange = createAsyncThunk<
  EventPayload<AssignmentRangeUpdated>,
  UpdateAssignmentRange,
  { state: RootState }
>(
  "talents/updateAssignmentRange",
  async (data, { requestId }): Promise<EventPayload<AssignmentRangeUpdated>> =>
    await updateAssignmentRangeAPI(data, { requestId }),
);

export const updateContinuousEmploymentRange = createAsyncThunk<
  EventPayload<unknown>[],
  UpdateContinuousEmploymentRange & LocallyHandledErrors,
  { state: RootState }
>(
  "talents/updateContinuousEmploymentRange",
  async (
    { locallyHandledErrors, ...data },
    { requestId },
  ): Promise<EventPayload<unknown>[]> => {
    const ret = await updateContinuousEmploymentRangeAPI(data, { requestId });
    //console.log("updateContinuousEmploymentRange done", data, ret);
    return ret;
  },
);

export const updateVehicleAvailable = createAsyncThunk<
  EventPayload<TalentVehicleAvailableUpdated>[],
  UpdateTalentVehicleAvailable,
  { state: RootState }
>(
  "talents/updateVehicleAvailable",
  async (
    data,
    { requestId },
  ): Promise<EventPayload<TalentVehicleAvailableUpdated>[]> =>
    await updateTalentVehicleAvailable(data, { requestId }),
);

export const updateAttachmentsInLocalStorage = createAction(
  "talents/updateAttachmentsInLocalStorage",
  (talentId: string) => {
    return { payload: { attachments: fetchAttachments(), talentId } };
  },
);

export const getAttachmentsFromLocalStorage = createAction(
  "talents/updateAttachmentsFromLocalStorage",
  (talentId: string) => {
    return { payload: { attachments: fetchAttachments(talentId), talentId } };
  },
);

export const getBlacklistByTalentId = createAsyncThunk<
  TalentBlacklist[],
  string,
  { state: RootState }
>(
  "talents/getBlacklistByTalentId",
  async (talentId: string): Promise<TalentBlacklist[]> => {
    return await blacklistsByTalentId(talentId);
  },
);

export const addBlacklistEntry = createAsyncThunk<
  EventPayload<BlacklistEntryAdded>,
  AddBlacklistEntry,
  { state: RootState }
>(
  "talents/addBlacklistEntry",
  async (
    blacklist: AddBlacklistEntry,
  ): Promise<EventPayload<BlacklistEntryAdded>> => {
    return await postAddBlacklistEntry(blacklist);
  },
);

export const removeBlacklistEntry = createAsyncThunk<
  EventPayload<BlacklistEntryRemoved>,
  RemoveBlacklistEntry,
  { state: RootState }
>(
  "talents/removeBlacklistEntry",
  async (
    blacklist: RemoveBlacklistEntry,
  ): Promise<EventPayload<BlacklistEntryRemoved>> => {
    return await postRemoveBlacklistEntry(blacklist);
  },
);

export const updateBlacklistEntry = createCommandAsyncThunk<
  UpdateBlacklistEntry,
  BlacklistEntryUpdated
>("talents/updateBlacklistEntry", updateBlacklistEntryCmd);

export const fetchExclusivitiesByTalentId = createQueryAsyncThunk<
  Paginated<TalentExclusivity[]>,
  ExclusivitiesByTalentIdFilters
>("talents/fetchExclusivitiesByTalentId", exclusivitiesByTalentId);

export const addExclusivityEntry = createCommandAsyncThunk<
  CreateExclusivityEntry,
  ExclusivityEntryAdded
>("talents/addExclusivityEntry", createExclusivityEntryCmd);

export const updateExclusivityEntry = createCommandAsyncThunk<
  UpdateExclusivityEntry,
  ExclusivityEntryUpdated
>("talents/updateExclusivityEntry", updateExclusivityEntryCmd);

export const removeExclusivityEntry = createCommandAsyncThunk<
  RemoveExclusivityEntry,
  ExclusivityEntryRemoved
>("talents/removeExclusivityEntry", removeExclusivityEntryCmd);

export const setTalentRecruiterCompany = createAsyncThunk<
  EventPayload<TalentRecruiterUpdated>[],
  SetTalentRecruiter,
  { state: RootState }
>(
  "talents/setTalentRecruiterCompany",
  async (
    body: SetTalentRecruiter,
    { requestId },
  ): Promise<EventPayload<TalentRecruiterUpdated>[]> =>
    await setTalentRecruiter(body, { requestId }),
);

export const fetchCandidateIntroductions = createQueryAsyncThunk<
  Paginated<TalentIntroduction[]>,
  CandidateIntroductionsByTalentIdFilters
>("talents/candidateIntroductions", candidateIntroductions);

export const introduceToCustomerWithoutOrder = createCommandAsyncThunk<
  IntroduceTalentsToCustomerWithoutOrder,
  CandidateWithoutOrderIntroducedToCustomer
>(
  "talents/introduceToCustomerWithoutOrder",
  introduceTalentsToCustomerWithoutOrder,
);

export const cancelIntroductionWithoutOrder = createCommandAsyncThunk<
  CancelTalentIntroductionWithoutOrder,
  CandidateWithoutOrderIntroductionCancelled
>(
  "talents/cancelIntroductionWithoutOrder",
  cancelTalentIntroductionWithoutOrder,
);

export const approveIntroductionWithoutOrder = createCommandAsyncThunk<
  ApproveIntroducedTalentWithoutOrder,
  CandidateWithoutOrderApprovedByCustomer
>(
  "talents/approveIntroductionWithoutOrder",
  approveIntroducedTalentWithoutOrder,
);

export const updateTalentStatus = createCommandAsyncThunk<
  UpdateTalentStatus,
  TalentStatusUpdated
>("talents/updateTalentStatus", postUpdateTalentStatus);

export const fetchTalentJobApplications = createAsyncThunk<
  TalentJobApplicationsResponse,
  TalentJobApplicationsParams,
  { state: RootState }
>("talentJobApplications/fetchTalentJobApplications", getTalentJobApplications);

export const setTalentWorkingArea = createCommandAsyncThunk<
  SetTalentWorkingAreaFromHome,
  TalentWorkingAreaEvents
>("talents/setTalentWorkingArea", setTalentWorkingAreaFromHome);

export const fetchTalentSelfServiceOrderPermissions = createAsyncThunk<
  TalentSelfServiceOrderPermission[],
  { talentId: string },
  { state: RootState }
>(
  "talents/fetchTalentSelfServiceOrderPermissions",
  async (filters: { talentId: string }, { requestId }) => {
    const response = await getTalentSelfServiceOrderPermissions(filters, {
      requestId,
    });
    return sortPermissions(response.permissions);
  },
);

export const addSelfServiceOrderPermission = createCommandAsyncThunk<
  AddSelfServiceOrderPermission,
  SelfServiceOrderPermissionAdded
>("talents/addSelfServiceOrderPermission", addSelfServiceOrderPermissionAPI);

export const updateSelfServiceOrderPermission = createCommandAsyncThunk<
  UpdateSelfServiceOrderPermission,
  SelfServiceOrderPermissionUpdated
>(
  "talents/updateSelfServiceOrderPermission",
  updateSelfServiceOrderPermissionAPI,
);

export const removeSelfServiceOrderPermission = createCommandAsyncThunk<
  RemoveSelfServiceOrderPermission,
  TalentSelfServiceOrderPermissionDeleted
>(
  "talents/removeSelfServiceOrderPermission",
  removeSelfServiceOrderPermissionAPI,
);

export const fetchWorkAgreementTemplateData = createQueryAsyncThunk<
  WorkAgreementTemplateData,
  WorkAgreementTemplateDataParams
>("talents/fetchWorkAgreementTemplateData", getWorkAgreementTemplateData);
export const fetchTalentPreview = createQueryAsyncThunk<
  TalentPreview,
  TalentIdQueryParameter
>("talents/fetchTalentPreview", getTalentPreviewById);

export const fetchCvDownloadUrl = createQueryAsyncThunk<
  DocumentDownloadUrl,
  TalentCvParams
>("talents/fetchCvDownloadUrl", getTalentCVDownloadUrl);

export const fetchCvUploadUrl = createQueryAsyncThunk<
  DocumentUploadUrl,
  CvDocumentUploadUrlQuery
>("talents/fetchCvUploadUrl", getTalentCVUploadUrl);

export const uploadCvDocument = createAsyncThunk<
  string,
  { url: string; content: Blob },
  { state: RootState }
>(
  "talents/uploadCvDocument",
  async (attachment: { url: string; content: Blob }, { requestId }) => {
    await uploadTalentCV(attachment.url, attachment.content);
    const filename = decodeURIComponent(
      attachment.url
        .substring(0, attachment.url.lastIndexOf("?"))
        .substring(attachment.url.lastIndexOf("/") + 1),
    );
    return filename;
  },
);

export const removeCvDocument = createCommandAsyncThunk<TalentCvParams, any>(
  "talents/removeCvDocument",
  removeTalentCVDocument,
);

export const fetchTalentLog = createQueryAsyncThunk<
  TalentLogEvent[],
  TalentIdQueryParameter
>("talents/fetchTalentLog", getTalentLogs);

const MAX_VALID_JS_DATE_EPOCH = 8640000000000000;
const assignmentEndFromEvent = (end: number): number | undefined =>
  end >= MAX_VALID_JS_DATE_EPOCH ? undefined : end;

// TODO move to somewhere else
export const fetchCoreUsers = createAsyncThunk<
  Paginated<CoreUser[]>,
  CoreUsersQueryParameters,
  { state: RootState }
>(
  "talents/fetchCoreUsers",
  async (filters: CoreUsersQueryParameters, { requestId }) =>
    getCoreUsers(filters, { requestId }) as Promise<Paginated<CoreUser[]>>,
);

const applyAssignmentRangeUpdate = (
  state: SliceState,
  action: PayloadAction<EventPayload<AssignmentRangeUpdated>>,
  source: string,
) => {
  if (action.payload?.name === "assgnRangeUpd") {
    state.assignment.details.end = assignmentEndFromEvent(
      action.payload.payload.newRange.end,
    );
    state.assignment.details.start = action.payload.payload.newRange.start;
    state.assignment.details.version = action.payload.version;
    state.status = "succeeded";
  } else {
    console.error(`Unexpected payload in ${source}`, action.payload);
    state.status = "failed";
  }
};

export const talentsSlice = createSlice({
  name: "talents",
  initialState: talentsAdapter.getInitialState({
    status: "idle",
    certificateDefinitions: [] as CertificateDefinition[],
    certificateDefinitionsStatus: "idle" as const,
  }) as SliceState,
  reducers: {
    // TODO: change state type to optional and handle state reset better
    resetAssignmentDetails: (state) => {
      state.assignment = {
        ...state.assignment,
        details: {
          version: 0,
          start: 0,
          end: 0,
          talentFirstName: "",
          talentLastName: "",
          contractId: "",
          contractName: "",
          tesId: "",
          orderAttributes: undefined,
          tesSelections: [],
          workTitleId: "",
          workLocationId: "",
          workLocationName: "",
          customerOrgId: "",
          customerOrgName: "",
          custCostCenters: [],
          salaryOverride: {},
          availableEntryTypes: [],
          invoicingPriceOverride: undefined,
          isShift: true,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateEezyReview.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;

      const talent = state.entities[talentId] as Talent;

      talent.eezyReview = {
        version: action.payload.version,
        value: action.payload.payload.eezyReview,
      };

      talentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: talent,
      });
      state.status = "succeeded";
    });
    builder.addCase(updateEezyReview.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateEezyReview.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentHrRep.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;

      const talent = state.entities[talentId] as Talent;

      talent.hrRepresentative = {
        version: action.payload.version,
        id: action.payload.payload.hrRepresentative,
      };

      talentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: talent,
      });
      state.status = "succeeded";
    });
    builder.addCase(updateTalentHrRep.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentHrRep.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchTalents.fulfilled, (state, action) => {
      talentsAdapter.setAll(state, action.payload);
      state.status = "succeeded";
    });
    builder.addCase(fetchTalent.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchTalent.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchTalent.fulfilled, (state, action) => {
      const talent = action.payload;

      if (!!talent.educations && talent.educations.values.length > 0) {
        talent.educations.values = identified(talent.educations.values);
      }

      if (!!talent.languages && talent.languages.values.length > 0) {
        talent.languages.values = identified(talent.languages.values);
      }

      if (!!talent.recommenders && talent.recommenders.values.length > 0) {
        talent.recommenders.values = identified(talent.recommenders.values);
      }

      talentsAdapter.setOne(state, talent);
      state.status = "succeeded";
    });
    builder.addCase(
      fetchTalentCertificateDocuments.fulfilled,
      (state, action) => {
        const { talentId } = action.meta.arg;
        // @ts-ignore
        const talent = state.entities[talentId] as Talent;
        talent.certificates = action.payload.certificates;
        talentsAdapter.setOne(state, talent);
      },
    );
    builder.addCase(updateTalentAccountAddress.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentAccountAddress.fulfilled, (state, action) => {
      const talent = state.entities[action.payload.id] as Talent;
      talent.account.address = action.payload.payload.address;

      talentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: talent,
      });
      state.status = "succeeded";
    });
    builder.addCase(updateTalentAccount.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentAccount.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentAccount.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      action.payload.forEach((event) => {
        const version = event.version as number;
        switch (event.name) {
          case "idn-talentAccount-addressUpdated":
            if (event.payload.address) {
              talent.account.address = event.payload.address;
              talent.account.version = version;
            }
            break;
          case "idn-talentAccount-nameUpdated":
            talent.account.firstName = event.payload.firstName as string;
            talent.account.lastName = event.payload.lastName as string;
            talent.account.version = version;
            break;
          case "idn-talentAccount-emailAddressUpdated":
            talent.account.emailAddress = event.payload.emailAddress as string;
            talent.account.version = version;
            break;
          case "idn-talentAccount-phoneNumberUpdated":
            talent.account.phoneNumber = event.payload.phoneNumber as string;
            talent.account.version = version;
            break;
          case "idn-talentAccount-dateOfBirthUpdated":
            talent.account.dob = event.payload.dob as string;
            talent.account.version = version;
            break;
          case "idn-talentAccount-nationalityUpdated":
            talent.account.nationality = event.payload.nationality as string;
            talent.account.version = version;
            break;
          default:
            break;
        }
      });

      state.status = "succeeded";
    });
    builder.addCase(fetchTalentsAutocomplete.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchTalentsAutocomplete.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchTalentsAutocompleteWithHr.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchTalentsAutocompleteWithHr.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchTalentsAutocompleteWithHr.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchTalentsPage.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchTalentsPage.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchTalentsPage.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(updateTalentCoverLetter.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentCoverLetter.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      talent.coverLetter = {
        version: action.payload.version,
        value: action.payload.payload.coverLetter,
      };

      talentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(updateTalentCoverLetter.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentWorkTitles.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentWorkTitles.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      const newVersion = action.payload.version;

      const givenWorkTitles = action.meta.arg.workTitles;
      const responseWorkTitleIds = action.payload.payload.workTitles;

      const updatedWorkTitles = givenWorkTitles.filter((given) => {
        return responseWorkTitleIds.find(
          (responseId) => given.id === responseId,
        );
      });

      talent.workTitles = {
        values: updatedWorkTitles,
        version: newVersion,
      };

      talentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(updateTalentWorkTitles.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentSkills.pending, (state, action) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentSkills.fulfilled, (state, action) => {
      const { talentId, skills } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;
      const responseSkillIds = action.payload.payload.skills;

      const updatedSkills = skills.filter((given) => {
        return responseSkillIds.find((responseId) => given.id === responseId);
      });

      const newVersion = action.payload.version;

      talent.skills = {
        values: updatedSkills,
        version: newVersion,
      };

      // FIXME: No updateOne and still works?

      state.status = "succeeded";
    });
    builder.addCase(updateTalentSkills.rejected, (state, action) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentEducations.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentEducations.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      const newVersion = action.payload.version;
      const updatedEducations = action.payload.payload.educations;

      talent.educations = {
        version: newVersion,
        values: identified(updatedEducations),
      };

      talentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(updateTalentEducations.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentRecommenders.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentRecommenders.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentRecommenders.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      const newVersion = action.payload.version;
      const updatedRecommenders = action.payload.payload.recommenders;

      talent.recommenders = {
        version: newVersion,
        values: identified(updatedRecommenders),
      };

      talentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(updateTalentLanguages.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentLanguages.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      const newVersion = action.payload.version;
      const updatedLanguages = action.payload.payload.languages;

      talent.languages = {
        version: newVersion,
        values: identified(updatedLanguages),
      };

      talentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(updateTalentLanguages.rejected, (state, action) => {
      state.status = "failed";
    });
    builder.addCase(addTalentTraining.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(addTalentTraining.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(addTalentTraining.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      const event = action.payload;
      const eventPayload = event.payload;

      const addedTraining: TalentTraining = {
        id: event.id,
        version: event.version,
        type: eventPayload.type,
        completed: eventPayload.completed,
        completedAt: eventPayload.completedAt,
        validTo: eventPayload.validTo,
      };

      const existingTrainings = talent.trainings || [];

      talent.trainings = [...existingTrainings, addedTraining];

      talentsAdapter.updateOne(state, {
        id: talent.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(updateTalentTraining.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentTraining.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentTraining.fulfilled, (state, action) => {
      const event = action.payload;
      const eventPayload = action.payload.payload;

      const talent = state.entities[eventPayload.talentId] as Talent;

      const existingTrainings = talent.trainings || [];

      talent.trainings = existingTrainings.map((training) => {
        if (training.id === event.id) {
          return {
            id: event.id,
            version: event.version,
            type: eventPayload.type,
            completed: eventPayload.completed,
            completedAt: eventPayload.completedAt,
            validTo: eventPayload.validTo,
          };
        }

        return training;
      });

      talentsAdapter.updateOne(state, {
        id: talent.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(removeTalentTraining.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(removeTalentTraining.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(removeTalentTraining.fulfilled, (state, action) => {
      const event = action.payload;
      const eventPayload = action.payload.payload;

      const talent = state.entities[eventPayload.talentId] as Talent;

      const targetTrainingId = event.id;

      const existingTrainings = talent.trainings || [];

      talent.trainings = existingTrainings.filter(
        (training) => training.id !== targetTrainingId,
      );

      talentsAdapter.updateOne(state, {
        id: talent.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(addTalentCertificate.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(addTalentCertificate.fulfilled, (state, action) => {
      if (action.payload) {
        const events = action.payload;
        const addedEvent = events.find(
          (event) => event.name === "tal-cert-add",
        );
        const approvedEvent = events.find(
          (event) => event.name === "tal-cert-apd",
        );

        const highestVersion = events.reduce(
          (max, event) => Math.max(max, event.version),
          0,
        );

        const { id, payload: addedEventPayload } =
          addedEvent as EventPayload<TalentCertificateAdded>;
        const { talentId, ...rest } = addedEventPayload;
        const { requiresDocument } = action.meta.arg;
        const talent = state.entities[talentId] as Talent;

        const updatedValues = {
          version: highestVersion,
          id,
          requiresDocument,
          ...(approvedEvent && {
            approvedAt: today(isoFormat),
            approvedBy: action.meta.arg.grantedBy,
          }),
          ...rest,
        };

        if (talent) {
          !talent.certificates
            ? (talent.certificates = [updatedValues])
            : talent.certificates?.push(updatedValues);
        }
      }
      state.status = "succeeded";
    });
    builder.addCase(addTalentCertificate.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(getCertificateDocumentUploadUrl.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(
      getCertificateDocumentUploadUrl.fulfilled,
      (state, action) => {
        const { talentId, certificateId } = action.meta.arg;
        const item = localStorage.getItem(LOCAL_STORAGE_KEY);
        const fromLocalStorage: LocalStorageAttachment[] = !!item
          ? JSON.parse(item)
          : [];

        const fn = decodeURIComponent(
          action.payload.url
            .substring(0, action.payload.url.lastIndexOf("?"))
            .substring(action.payload.url.lastIndexOf("/") + 1),
        );
        const newAttachment = {
          id: fn,
          uploadStarted: Date.now(),
          path: "talents",
          pathId: [talentId, certificateId],
          filename: fn,
          status: "scanning",
        };
        const updated = [...fromLocalStorage, newAttachment];
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));

        state.status = "succeeded";
      },
    );
    builder.addCase(getCertificateDocumentUploadUrl.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(getCertificateDocumentDownloadUrl.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(getCertificateDocumentDownloadUrl.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(getCertificateDocumentDownloadUrl.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(uploadCertificate.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(uploadCertificate.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(uploadCertificate.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentCertificate.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentCertificate.fulfilled, (state, action) => {
      const { talentId, validTo } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      if (action.payload) {
        const currentCertificates = talent.certificates;
        const aggregateId = action.payload.id;
        const eventPayload: TalentCertificateUpdated = {
          ...action.payload.payload,
          ...(validTo ? { validTo } : { validTo: "" }),
        };

        const updatedCertificates: TalentCertificate[] | undefined =
          currentCertificates?.map((certificate: TalentCertificate) => {
            if (certificate.id === aggregateId) {
              return {
                ...certificate,
                ...eventPayload,
                version: action.payload.version,
              } as TalentCertificate;
            }
            return certificate;
          });

        talent.certificates = updatedCertificates;

        talentsAdapter.updateOne(state, {
          id: action.payload.id,
          changes: talent,
        });
      }

      state.status = "succeeded";
    });
    builder.addCase(updateTalentCertificate.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(removeTalentCertificate.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(removeTalentCertificate.fulfilled, (state, action) => {
      const { talentId } = action.payload.payload;
      const talent = state.entities[talentId] as Talent;

      const currentCertificates = talent.certificates;
      const aggregateId = action.payload.id;

      const updatedCertificates: TalentCertificate[] | undefined =
        currentCertificates?.filter(
          (certificate: TalentCertificate) => certificate.id !== aggregateId,
        );

      talent.certificates = updatedCertificates;

      talentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(removeTalentCertificate.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(removeTalentCertificateDocument.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(
      removeTalentCertificateDocument.fulfilled,
      (state, action) => {
        const events = action.payload;
        if (events.length) {
          // Doc deletion event should return in any case.
          // Cert unapprove or cert unreject returns only if the cert is originally approved or rejected.
          // From that we need the version number and to update the cert status
          const deleteEvent = events.find(
            (event) => event.name === "tal-cert-doc-del",
          )!!;
          const unapproveEvent = events.find(
            (event) => event.name === "tal-cert-unapd",
          );
          const unrejectEvent = events.find(
            (event) => event.name === "tal-cert-unrjd",
          );

          const { talentId, filename } = deleteEvent.payload;
          const aggregateId = deleteEvent.id;
          const version = events.reduce(
            (max, event) => Math.max(max, event.version),
            0,
          );

          const talent = state.entities[talentId] as Talent;
          const removeDocument = (certificate: TalentCertificate) => ({
            ...certificate,
            version,
            documents: certificate?.documents?.filter(
              (document) => document.filename !== filename,
            ),
          });

          const updatedCert = removeDocument(
            talent.certificates?.find(
              (certificate) => certificate.id === aggregateId,
            )!!,
          );
          if (unapproveEvent) {
            delete updatedCert.approvedAt;
            delete updatedCert.approvedBy;
          } else if (unrejectEvent) {
            delete updatedCert.rejectedAt;
            delete updatedCert.rejectedBy;
          }

          const updatedCertificates = talent.certificates?.map(
            (certificate) => {
              return certificate.id === aggregateId ? updatedCert : certificate;
            },
          );
          talent.certificates = updatedCertificates;

          talentsAdapter.updateOne(state, {
            id: talentId,
            changes: talent,
          });
        }

        state.status = "succeeded";
      },
    );
    builder.addCase(removeTalentCertificateDocument.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentApplicationLanguage.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentApplicationLanguage.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      updateTalentApplicationLanguage.fulfilled,
      (state, action) => {
        const { talentId } = action.meta.arg;
        const talent = state.entities[talentId] as Talent;

        const event = action.payload;

        const eventVersion = event.version;
        const eventPayload = event.payload;

        talent.applicationLanguage = {
          version: eventVersion,
          value: eventPayload.language,
        };

        talentsAdapter.updateOne(state, {
          id: action.payload.id,
          changes: talent,
        });

        state.status = "succeeded";
      },
    );
    builder.addCase(updateTalentGdprProperties.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentGdprProperties.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentGdprProperties.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      const event = action.payload;

      const eventVersion = event.version;
      const eventPayload = event.payload;

      talent.gdprProperties = {
        version: eventVersion,
        disableAutomatching: eventPayload.disableAutomatching,
        forbidPersonalInfoUpdates: eventPayload.forbidPersonalInfoUpdates,
      };

      talentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(approveTalentCertificate.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(approveTalentCertificate.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(approveTalentCertificate.fulfilled, (state, action) => {
      if (action.payload?.payload) {
        const { talentId } = action.payload.payload;
        const { user } = action.meta.arg;
        const talent = state.entities[talentId] as Talent;

        const targetCertificateId = action.payload.id;
        const updatedVersion = action.payload.version;

        const currentCertificates = talent.certificates;

        talent.certificates = currentCertificates?.map((certificate) => {
          if (certificate.id === targetCertificateId) {
            delete certificate.rejectedAt;
            delete certificate.rejectedBy;
            certificate.approvedAt = today(isoFormat);
            certificate.approvedBy = user
              ? `${user.firstName} ${user.lastName}`
              : undefined;
            certificate.version = updatedVersion;
            return certificate;
          }

          return certificate;
        });

        talentsAdapter.updateOne(state, {
          id: talentId,
          changes: talent,
        });
      }

      state.status = "succeeded";
    });
    builder.addCase(rejectTalentCertificate.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(rejectTalentCertificate.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(rejectTalentCertificate.fulfilled, (state, action) => {
      const { talentId } = action.payload.payload;
      const { user } = action.meta.arg;

      const talent = state.entities[talentId] as Talent;

      const targetCertificateId = action.payload.id;
      const updatedVersion = action.payload.version;

      const currentCertificates = talent.certificates;

      talent.certificates = currentCertificates?.map((certificate) => {
        if (certificate.id === targetCertificateId) {
          delete certificate.approvedAt;
          delete certificate.approvedBy;
          certificate.rejectedAt = today(isoFormat);
          certificate.rejectedBy = user
            ? `${user.firstName} ${user.lastName}`
            : undefined;
          certificate.version = updatedVersion;
          return certificate;
        }

        return certificate;
      });

      talentsAdapter.updateOne(state, {
        id: talentId,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(updateTalentWorkRight.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentWorkRight.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentWorkRight.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      const event = action.payload;

      const eventVersion = event.version;
      const eventPayload = event.payload;

      talent.workRight = {
        version: eventVersion,
        reason: eventPayload.reason,
        start: eventPayload.start,
        end: eventPayload.end,
      };

      talentsAdapter.updateOne(state, {
        id: talent.id,
        changes: talent,
      });

      state.status = "succeeded";
    });
    builder.addCase(removeTalentSsn.fulfilled, (state, action) => {
      const info = state.entities[action.meta.arg.talentId]?.salaryInformation;
      const event = action.payload.find(
        (event) => event.name === "idn-talentSalaryInfo-ssnDel",
      );
      if (info && event) {
        delete info.ssn;
        info.version = event.version;
      }
    });
    builder.addCase(removeTalentBankInfo.fulfilled, (state, action) => {
      const info = state.entities[action.meta.arg.talentId]?.salaryInformation;
      const event = action.payload.find(
        (event) => event.name === "idn-talentSalaryInfo-bankInfoDel",
      );
      if (info && event) {
        delete info.iban;
        delete info.bic;
        info.version = event.version;
      }
    });
    builder.addCase(updateTalentSalaryInformation.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentSalaryInformation.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      updateTalentSalaryInformation.fulfilled,
      (state, action) => {
        const { talentId } = action.meta.arg;
        const talent = state.entities[talentId] as Talent;

        const events = action.payload;

        events.forEach((event) => {
          const version = event.version;

          switch (event.name) {
            case "idn-talentSalaryInfo-my-ssnUpdated":
              const talentSsnUpdated = event.payload as TalentSsnUpdated;
              if (talent.salaryInformation) {
                talent.salaryInformation.ssn = talentSsnUpdated.ssn;
                talent.salaryInformation.version = version;
              } else {
                talent.salaryInformation = {
                  ssn: talentSsnUpdated.ssn,
                  version: version,
                };
              }
              break;
            case "idn-talentSalaryInfo-my-genderUpdated":
              const talentGenderUpdated = event.payload as TalentGenderUpdated;
              if (talent.salaryInformation) {
                talent.salaryInformation.gender =
                  talentGenderUpdated.gender as Gender;
                talent.salaryInformation.version = version;
              } else {
                talent.salaryInformation = {
                  gender: talentGenderUpdated.gender,
                  version: version,
                };
              }
              break;
            case "idn-talentSalaryInfo-my-bankInfoUpdated":
              const talentBankInfoUpdated =
                event.payload as TalentBankInfoUpdated;
              if (talent.salaryInformation) {
                talent.salaryInformation.iban = talentBankInfoUpdated.iban;
                talent.salaryInformation.bic = talentBankInfoUpdated.bic;
                talent.salaryInformation.version = version;
              } else {
                talent.salaryInformation = {
                  iban: talentBankInfoUpdated.iban,
                  bic: talentBankInfoUpdated.bic,
                  version: version,
                };
              }
              break;
            default:
              break;
          }
        });

        state.status = "succeeded";
      },
    );

    builder.addCase(updateTalentTaxInformation.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentTaxInformation.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentTaxInformation.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      const event = action.payload;
      const eventPayload = event.payload;

      talent.taxInformation = {
        version: event.version,
        taxNonresidentAddress: eventPayload.taxNonresidentAddress,
        taxNumber: eventPayload.taxNumber,
        insurancePaymentOutsideA1: eventPayload.insurancePaymentOutsideA1,
      };

      state.status = "succeeded";
    });
    builder.addCase(updateTalentTaxDeductionCard.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentTaxDeductionCard.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentTaxDeductionCard.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;

      const event = action.payload;
      const eventPayload = event.payload;

      talent.taxDeductionCard = {
        version: event.version,
        taxDeductionCard: {
          updatedBy: eventPayload.taxDeductionCard.updatedBy,
          updatedAt: eventPayload.taxDeductionCard.updatedAt,
          validFrom: eventPayload.taxDeductionCard.validFrom,
          withholdingRate: eventPayload.taxDeductionCard.withholdingRate,
          incomeCeiling: eventPayload.taxDeductionCard.incomeCeiling,
          additionalWithholdingRate:
            eventPayload.taxDeductionCard.additionalWithholdingRate,
        },
      };

      state.status = "succeeded";
    });
    builder.addCase(fetchAssignmentById.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(
      fetchEezyCompaniesAutocomplete.fulfilled,
      (state, action) => {
        state.status = "succeeded";
      },
    );
    builder.addCase(fetchEezyCompaniesAutocomplete.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchEezyCompaniesAutocomplete.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchAssignmentById.fulfilled, (state, action) => {
      const assignment = { ...action.payload };
      state.assignment = { ...assignment };
      state.status = "succeeded";
    });
    builder.addCase(fetchAssignmentById.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(changeAssignmentTesSel.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(changeAssignmentTesSel.fulfilled, (state, action) => {
      state.assignment.details.version = action.payload.version;
      state.assignment.details.tesSelections =
        action.payload.payload.tesSelections;
      state.status = "succeeded";
    });
    builder.addCase(changeAssignmentTesSel.rejected, (state) => {
      state.status = "failed";
    });

    builder.addCase(postAddAssignmentSupplement.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(postAddAssignmentSupplement.fulfilled, (state, action) => {
      // Get events from response
      const events = [...action.payload];

      // Look through edit events and update the state based on assignmentId
      events.forEach((event: any) => {
        if (
          event.name === "assgnSupAdd" &&
          action.meta.arg.assignmentId === event.id
        ) {
          // Update assignment
          const updatedAssignment = {
            ...state.assignment,
            details: {
              ...state.assignment.details,
              version: event.version,
            },
          };

          // Update or initialize the supplements entry if present in the response
          if (event.payload.supplements) {
            Object.keys(event.payload.supplements).forEach((supplementName) => {
              if (!updatedAssignment.supplements[supplementName]) {
                updatedAssignment.supplements[supplementName] = {
                  isActive: event.payload.supplements[supplementName].isActive,
                  definition:
                    event.payload.supplements[supplementName].definition,
                  mode: event.payload.supplements[supplementName].mode,
                };
              } else {
                updatedAssignment.supplements[supplementName].mode =
                  event.payload.supplements[supplementName].mode;
              }
            });
          }

          // Update state
          state.assignment = { ...updatedAssignment };
        }
      });
      state.status = "succeeded";
    });
    builder.addCase(postAddAssignmentSupplement.rejected, (state) => {
      state.status = "failed";
    });

    builder.addCase(postEditAssignmentSupplement.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(postEditAssignmentSupplement.fulfilled, (state, action) => {
      // Get events from response
      const events = [...action.payload];

      // Look through edit events and update the state based on assignmentId
      events.forEach((event: any) => {
        if (
          event.name === "assgnSupMode" &&
          action.meta.arg.assignmentId === event.id
        ) {
          // Update assignment
          const assignment = {
            ...state.assignment,
            details: {
              ...state.assignment.details,
              version: event.version,
            },
          };

          // Update supplement of assignment
          assignment.supplements[event.payload.code].mode = event.payload.mode;

          // Update state
          state.assignment = { ...assignment };
        }
      });
      state.status = "succeeded";
    });
    builder.addCase(postEditAssignmentSupplement.rejected, (state) => {
      state.status = "failed";
    });

    builder.addCase(postDeleteAssignmentSupplement.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(
      postDeleteAssignmentSupplement.fulfilled,
      (state, action) => {
        // Get events from response
        const events = [...action.payload];

        // Look through edit events and update the state based on assignmentId
        events.forEach((event: any) => {
          if (
            event.name === "assgnSupDel" &&
            action.meta.arg.assignmentId === event.id
          ) {
            // Update assignment
            const updatedAssignment = {
              ...state.assignment,
              details: {
                ...state.assignment.details,
                version: event.version,
              },
            };

            // Delete supplements from assignment
            event.payload.codes.forEach((code: string) => {
              delete updatedAssignment.supplements[code];
            });
            // Update state
            state.assignment = { ...updatedAssignment };
          }
        });
        state.status = "succeeded";
      },
    );
    builder.addCase(postDeleteAssignmentSupplement.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(addManualSalary.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(addManualSalary.rejected, (state) => {
      state.status = "failed";
    });

    builder.addCase(addManualSalary.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(editManualSalary.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(editManualSalary.rejected, (state) => {
      state.status = "failed";
    });

    builder.addCase(editManualSalary.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(deleteManualSalary.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(deleteManualSalary.rejected, (state) => {
      state.status = "failed";
    });

    builder.addCase(deleteManualSalary.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(
      fetchEmploymentDetailsForManualSalary.fulfilled,
      (state) => {
        state.status = "succeeded";
      },
    );
    builder.addCase(fetchEmploymentDetailsForManualSalary.rejected, (state) => {
      state.status = "failed";
    });

    builder.addCase(fetchEmploymentDetailsForManualSalary.pending, (state) => {
      state.status = "pending";
    });
    // TODO: move to employmentsSlice?
    builder.addCase(fetchEmploymentsHierarchy.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchEmploymentsHierarchy.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchEmploymentsHierarchy.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(changeAssignmentOrderAttributes.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(
      changeAssignmentOrderAttributes.fulfilled,
      (state, action) => {
        if (action.payload) {
          action.payload.forEach((event) => {
            if (
              event.name === "assgnOrdAttrChg" &&
              "orderAttributes" in event.payload
            ) {
              state.assignment.details.orderAttributes =
                event.payload.orderAttributes;
            }
            if (
              event.name === "assgnCustRecChg" &&
              "isCustomerRecruit" in event.payload
            ) {
              state.assignment.details.isCustomerRecruit =
                event.payload.isCustomerRecruit;
            }
            state.assignment.details.version = event.version;
          });
        }

        state.status = "succeeded";
      },
    );
    builder.addCase(changeAssignmentOrderAttributes.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(changeAssignmentCostCenters.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(changeAssignmentCostCenters.fulfilled, (state, action) => {
      const { custCostCenters } = action.meta.arg;
      const formattedCustCostCenters = custCostCenters.map((costCenter) => ({
        name: costCenter.name,
        id: costCenter.id,
      }));
      state.assignment.details.custCostCenters = formattedCustCostCenters;
      state.assignment.details.version = action.payload.version;
      state.status = "succeeded";
    });
    builder.addCase(changeAssignmentCostCenters.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(changeAssignmentSalaryOverride.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(
      changeAssignmentSalaryOverride.fulfilled,
      (state, action) => {
        if (action.payload) {
          if ("salaryOverride" in action.payload.payload) {
            state.assignment.details.salaryOverride =
              action.payload.payload.salaryOverride;
          } else {
            state.assignment.details.salaryOverride = undefined;
          }

          state.assignment.details.version = action.payload.version;
        }
        state.status = "succeeded";
      },
    );
    builder.addCase(changeAssignmentSalaryOverride.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(changeAssignmentInvPrice.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(changeAssignmentInvPrice.fulfilled, (state, action) => {
      if (action.payload) {
        // If we have unitPrice in payload, update invoicingPriceOverride state value with it.
        // Else invoicingPriceOverride is undefined.
        if ("unitPrice" in action.payload.payload) {
          state.assignment.details.invoicingPriceOverride =
            action.payload.payload.unitPrice;
        } else {
          state.assignment.details.invoicingPriceOverride = undefined;
        }
        // Increase version
        state.assignment.details.version = action.payload.version;
      }
      state.status = "succeeded";
    });
    builder.addCase(changeAssignmentInvPrice.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(endAssignment.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(endAssignment.fulfilled, (state, action) => {
        console.log("endAssignment action", action.meta);
        applyAssignmentRangeUpdate(state, action, "endAssignment");
    });
    builder.addCase(endAssignment.rejected, (state, action) => {
      state.error = action.error.message;
      state.status = "failed";
    });
    builder.addCase(updateAssignmentRange.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateAssignmentRange.fulfilled, (state, action) => {
      console.log("updateAssignmentRange action", action.meta);
      applyAssignmentRangeUpdate(state, action, "updateAssignmentRange");
    });
    builder.addCase(updateAssignmentRange.rejected, (state, action) => {
      state.error = action.error.message;
      state.status = "failed";
    });
    builder.addCase(updateContinuousEmploymentRange.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateContinuousEmploymentRange.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(
      updateContinuousEmploymentRange.rejected,
      (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      },
    );
    builder.addCase(postSplitAssignment.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(postSplitAssignment.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(postSplitAssignment.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateVehicleAvailable.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateVehicleAvailable.fulfilled, (state, action) => {
      if (action.payload) {
        action.payload.forEach((event) => {
          const talentId = event.id;
          if (event.name === "tal-vehAvail-upd") {
            const talent = state.entities[talentId] as Talent;

            talent.vehicleAvailable = {
              version: event.version,
              value: event.payload.vehicleAvailable,
            };

            talentsAdapter.updateOne(state, {
              id: talentId,
              changes: talent,
            });
          }
        });
      }
      state.status = "succeeded";
    });
    builder.addCase(updateVehicleAvailable.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(getAttachmentsFromLocalStorage, (state, action) => {
      // Get attachments from local storage to redux store
      const { talentId, attachments: fromStorage } = action.payload;
      // @ts-ignore
      const talent = state.entities[talentId] as Talent;
      const certificates = talent.certificates || [];
      const cv = talent.cv;

      // Don't add attachments that are already in the state or over five minutes old
      const newCertFiles = fromStorage.filter(
        (attachment) =>
          Date.now() - attachment.uploadStarted < 300000 &&
          certificates.find((c) => c.id === attachment.pathId[1]),
      );
      const newCvFiles = fromStorage.filter(
        (attachment) =>
          attachment.pathId[1] === "cv" &&
          Date.now() - attachment.uploadStarted < 300000,
      );
      const newCv = !!newCvFiles.length
        ? {
            pathspec: "",
            filename: newCvFiles[0].filename,
            size: 0,
            fileType: "",
            status: "scanning",
          }
        : cv;
      // Update existing certificate document and add new ones
      const certificateDocsOldAndNew = certificates.map((certificate) => {
        const { documents } = certificate;
        const newDocs: TalentCertificateDocument[] = newCertFiles
          .filter((newDoc) => newDoc.pathId[1] === certificate.id)
          .map((attachment) => ({
            pathspec: "",
            filename: attachment.filename,
            size: 0,
            fileType: "",
            status: attachment.status,
          }));
        const merged = documents?.length ? [...documents, ...newDocs] : newDocs;
        return {
          ...certificate,
          documents: merged.reduce((acc, curr) => {
            const exists = acc.find((doc) => doc.filename === curr.filename);
            if (exists) {
              exists.status = curr.status;
            } else {
              acc.push(curr);
            }
            return acc;
          }, [] as TalentCertificateDocument[]),
        };
      });
      talent.certificates = [...certificateDocsOldAndNew];
      talent.cv = newCv;
    });

    builder.addCase(updateAttachmentsInLocalStorage, (state, action) => {
      // Refresh attachments in local storage
      const { talentId, attachments: localStorageData } = action.payload;
      // @ts-ignore
      const talent = state.entities[talentId] as Talent;
      const certificates = talent.certificates || [];
      const cv = talent.cv;

      const attachments =
        localStorageData &&
        localStorageData.filter((attachment) => attachment.path === "talents");
      const otherTalentsAttachments = attachments.filter(
        (attachment) => attachment.pathId[0] !== talent.id,
      );

      // Remove items that are over five minutes old (probably failed in virus scan)
      // and items that are finished uploading to server.
      const updatedCertificates = attachments.filter(
        (attachment) =>
          attachment.pathId[0] === talent.id &&
          attachment.pathId[1] !== "cv" &&
          Date.now() - attachment.uploadStarted < 300000 &&
          !certificates
            .find((certificate) => certificate.id === attachment.pathId[1])
            ?.documents?.find(
              (doc) =>
                doc.filename === attachment.filename &&
                attachment.status === "scanning",
            ),
      );

      const cvInStore = attachments.filter(
        (attachment) =>
          attachment.pathId[0] === talent.id &&
          attachment.pathId[1] === "cv" &&
          Date.now() - attachment.uploadStarted < 300000,
      );
      const updatedCv =
        !cv || cv.filename === cvInStore[0]?.filename ? cvInStore : [];
      const updatedTalentAttachments = [...updatedCertificates, ...updatedCv];
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify([
          ...updatedTalentAttachments,
          ...otherTalentsAttachments,
        ]),
      );
    });

    builder.addCase(getBlacklistByTalentId.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(getBlacklistByTalentId.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(getBlacklistByTalentId.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(addBlacklistEntry.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(addBlacklistEntry.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(addBlacklistEntry.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(removeBlacklistEntry.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(removeBlacklistEntry.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(removeBlacklistEntry.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(updateBlacklistEntry.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateBlacklistEntry.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(updateBlacklistEntry.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(setTalentRecruiterCompany.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(setTalentRecruiterCompany.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(setTalentRecruiterCompany.fulfilled, (state, action) => {
      if (action.payload) {
        action.payload.forEach((event) => {
          if (event.name === "talRecUpd") {
            const { talentId } = action.meta.arg;
            const talent = state.entities[talentId] as Talent;
            talent.recruitedByCustomerCompany =
              event.payload?.customerCompanyId || undefined;
          }
        });
      }
      state.status = "succeeded";
    });
    builder.addCase(updateTalentStatus.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(updateTalentStatus.rejected, (state, action) => {
      state.status = "failed";
    });
    builder.addCase(updateTalentStatus.fulfilled, (state, action) => {
      if (action.payload) {
        action.payload.forEach((event) => {
          const { talentId } = action.meta.arg;
          const talent = state.entities[talentId] as Talent;
          talent.account.status = event.payload.status;
          talent.account.statusChanged = format(
            event.payload?.date || new Date(),
            "yyyy-MM-dd",
          );
          talent.account.version = event.version;
        });
      }
      state.status = "succeeded";
    });
    builder.addCase(
      postChangeAssignmentSupplementPriceOverrides.fulfilled,
      (state, action) => {
        // Get events from response
        const events = [...action.payload];

        // Look through edit events and update the state based on assignmentId
        events.forEach((event: any) => {
          if (
            event.name === "assgnSupPrcChg" &&
            action.meta.arg.assignmentId === event.id
          ) {
            // Update assignment
            const assignment = {
              ...state.assignment,
              details: {
                ...state.assignment.details,
                version: event.version,
              },
            };

            // Update supplementPriceOverrides of assignment
            assignment.supplementPriceOverrides = event.payload.overrides;

            // Update state
            state.assignment = { ...assignment };
            state.status = "succeeded";
          }
        });
      },
    );
    builder.addCase(
      postChangeAssignmentSupplementPriceOverrides.pending,
      (state) => {
        state.status = "pending";
      },
    );
    builder.addCase(
      postChangeAssignmentSupplementPriceOverrides.rejected,
      (state) => {
        state.status = "failed";
      },
    );
    builder.addCase(
      postChangeAssignmentSupplementPriceSchedule.fulfilled,
      (state, action) => {
        const event = action.payload.find(
          (e: any) =>
            e.name === "assgnSupPrcChgV2" &&
            action.meta.arg.assignmentId === e.id,
        );
        if (event) {
          state.assignment = {
            ...state.assignment,
            details: {
              ...state.assignment.details,
              version: event.version,
            },
          };
        }
        state.status = "succeeded";
      },
    );
    builder.addCase(
      postChangeAssignmentSupplementPriceSchedule.pending,
      (state) => {
        state.status = "pending";
      },
    );
    builder.addCase(
      postChangeAssignmentSupplementPriceSchedule.rejected,
      (state) => {
        state.status = "failed";
      },
    );
    builder.addCase(fetchTalentJobApplications.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchTalentJobApplications.fulfilled, (state, action) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchTalentJobApplications.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(setTalentWorkingArea.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(setTalentWorkingArea.fulfilled, (state, action) => {
      if (action.payload) {
        action.payload.forEach((event) => {
          const { talentId } = action.meta.arg;
          const talent = state.entities[talentId] as Talent;

          if (event.name === "tal-wrk-area-cfg-area-rad-upd") {
            const typedEvent =
              event as EventPayload<TalentWorkingAreaRadiusFromHomeLocationUpdated>;
            const updatedWorkingAreas = {
              config: {
                distanceFromHome: typedEvent.payload.radiusInMeters,
                version: typedEvent.version,
              },
            };
            talent.workingAreas = updatedWorkingAreas;
          }
          if (event.name === "tal-wrk-area-cfg-area-rad-del") {
            const updatedWorkingAreas = {
              config: {
                version: event.version,
              },
            };
            talent.workingAreas = updatedWorkingAreas;
          }
        });
      }
      state.status = "succeeded";
    });
    builder.addCase(setTalentWorkingArea.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchWorkAgreementTemplateData.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchWorkAgreementTemplateData.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchWorkAgreementTemplateData.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchTalentPreview.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchTalentPreview.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchTalentPreview.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchCvDownloadUrl.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCvDownloadUrl.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchCvDownloadUrl.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchCvUploadUrl.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCvUploadUrl.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      // Add CV upload attachment to local storage
      const timestamp = formatDate(Date.now(), "yyyyMMddHHmmss");
      const filename = `${timestamp}_${talentId}_cv.pdf`;
      const newAttachment: LocalStorageAttachment = {
        id: filename,
        filename,
        uploadStarted: Date.now(),
        path: "talents",
        pathId: [talentId, "cv"],
        status: "scanning",
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([newAttachment]));
      state.status = "succeeded";
    });
    builder.addCase(fetchCvUploadUrl.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(uploadCvDocument.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(uploadCvDocument.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(uploadCvDocument.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(removeCvDocument.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(removeCvDocument.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      const talent = state.entities[talentId] as Talent;
      if (talent) {
        talent.cv = undefined;
        talentsAdapter.updateOne(state, {
          id: talentId,
          changes: talent,
        });
      }
      state.status = "succeeded";
    });
    builder.addCase(removeCvDocument.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchTalentCv.fulfilled, (state, action) => {
      const { talentId } = action.meta.arg;
      // @ts-ignore
      const talent = state.entities[talentId] as Talent;
      talent.cv = action.payload.cv;
      talentsAdapter.setOne(state, talent);

      // Remove CV attachment from local storage after CV is returned
      if (!action.payload.cv) return;
      const item = localStorage.getItem(LOCAL_STORAGE_KEY);
      const localStorageData: LocalStorageAttachment[] = !!item
        ? JSON.parse(item)
        : [];
      const allAttachments =
        localStorageData &&
        localStorageData.filter(
          (attachment) =>
            attachment.path === "talents" && attachment.pathId[0] === talentId,
        );
      const cvAttachment = allAttachments.filter(
        (attachment) => attachment.pathId[1] === "cv",
      );
      if (action.payload.cv.filename === cvAttachment[0]?.filename) {
        const filtered = allAttachments.filter(
          (attachment) =>
            !(
              attachment.pathId[0] === talentId && attachment.pathId[1] === "cv"
            ),
        );
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filtered));
      }
    });
    builder.addCase(fetchTalentLog.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchTalentLog.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(fetchTalentLog.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      fetchCertificateDefinitionsAutocomplete.pending,
      (state, action) => {
        if (action.meta.arg.term) return;
        state.certificateDefinitionsStatus = "pending";
      },
    );
    builder.addCase(
      fetchCertificateDefinitionsAutocomplete.fulfilled,
      (state, action) => {
        if (action.meta.arg.term) return;
        state.certificateDefinitions = action.payload;
        state.certificateDefinitionsStatus = "succeeded";
      },
    );
    builder.addCase(
      fetchCertificateDefinitionsAutocomplete.rejected,
      (state, action) => {
        if (action.meta.arg.term) return;
        state.certificateDefinitionsStatus = "failed";
      },
    );
  },
});

type Identified<T> = { id: string } & T;

const identified = <T>(values: T[]): Identified<T>[] =>
  values.map((value) => ({ ...value, id: nanoid() }));

// FIXME: is selectAllTalents dead code?
export const { selectAll: selectAllTalents, selectById: selectTalentById } =
  talentsAdapter.getSelectors((state: RootState) => state.talents);

export const selectTalentStatusById = (
  state: RootState,
  talentId?: string,
): Talent["account"]["status"] | undefined => {
  if (!talentId) return undefined;
  const talent = state.talents.entities[talentId] as Talent | undefined;
  return talent?.account.status;
};

export const selectCertificateDefinitions = (state: RootState) =>
  state.talents.certificateDefinitions ?? [];

export const selectCertificateDefinitionsStatus = (state: RootState) =>
  state.talents.certificateDefinitionsStatus ?? "idle";

export const { resetAssignmentDetails } = talentsSlice.actions;

export const talentsReducer = talentsSlice.reducer;
